import { t } from '../../i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeEmptyNotice } from './noticeStorage';
import { uploadNoticeImage } from './noticeApi';
import LoungeRichEditor, {
  createLoungeTextBlock,
  normalizeLoungeEditorBlocks,
} from '../lounge/LoungeRichEditor';

const RICH_NOTICE_TYPE = 'npc_notice_rich';
const RICH_NOTICE_VERSION = 1;

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function legacyTextBlock(text) {
  return {
    ...createLoungeTextBlock(),
    text: String(text || ''),
  };
}

function parseLegacyNoticeContent(content = '') {
  const raw = String(content || '');
  if (!raw.trim()) return [createLoungeTextBlock()];

  const regex = /\[img:([^\]]+)\]/g;
  const blocks = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(raw)) !== null) {
    const text = raw.slice(lastIndex, match.index);
    if (text) blocks.push(legacyTextBlock(text));
    blocks.push({
      id: uid(),
      type: 'image',
      url: String(match[1] || '').trim(),
      alt: '',
      display: { widthPercent: 100, heightPx: 280, objectFit: 'clip' },
    });
    lastIndex = regex.lastIndex;
  }

  const tail = raw.slice(lastIndex);
  if (tail) blocks.push(legacyTextBlock(tail));

  return normalizeLoungeEditorBlocks(blocks.length ? blocks : [legacyTextBlock(raw)]);
}

function parseNoticeEditorBlocks(content = '') {
  const raw = String(content || '').trim();
  if (!raw) return [createLoungeTextBlock()];

  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      parsed.type === RICH_NOTICE_TYPE &&
      Array.isArray(parsed.blocks)
    ) {
      return normalizeLoungeEditorBlocks(parsed.blocks);
    }
  } catch (_) {}

  return parseLegacyNoticeContent(content);
}

function serializeNoticeEditorBlocks(blocks) {
  return JSON.stringify({
    type: RICH_NOTICE_TYPE,
    version: RICH_NOTICE_VERSION,
    blocks: normalizeLoungeEditorBlocks(blocks),
  });
}

function hasMeaningfulContent(blocks) {
  return normalizeLoungeEditorBlocks(blocks).some((block) => {
    if (block?.type === 'image') return !!String(block.url || '').trim();
    if (block?.type !== 'text') return false;
    if (String(block.text || '').trim()) return true;
    return Array.isArray(block.segments)
      && block.segments.some((segment) => String(segment?.text || '').trim());
  });
}

export default function NoticeEditorModal({ open, mode, draft, onClose, onSave, saving = false }) {
  const [form, setForm] = useState(null);
  const [contentBlocks, setContentBlocks] = useState([createLoungeTextBlock()]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const contentImageRef = useRef(null);
  const insertAfterRef = useRef(null);

  const title = useMemo(
    () => (mode === 'edit' ? t('공지 수정') : t('새 공지 작성')),
    [mode]
  );

  useEffect(() => {
    if (!open) return;
    const nextForm = draft ? { ...draft } : makeEmptyNotice({ writer: 'admin' });
    setForm(nextForm);
    setContentBlocks(parseNoticeEditorBlocks(nextForm.content || ''));
    setUploadingImage(false);
    insertAfterRef.current = null;
  }, [open, draft]);

  if (!open || !form) return null;

  const disabled = saving || uploadingImage;

  const update = (patch) => {
    if (disabled) return;
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const submit = () => {
    if (disabled) return;
    if (!String(form.title || '').trim()) {
      alert(t('제목을 입력해줘.'));
      return;
    }
    if (!hasMeaningfulContent(contentBlocks)) {
      alert(t('본문을 입력해줘.'));
      return;
    }

    onSave({
      ...form,
      content: serializeNoticeEditorBlocks(contentBlocks),
    });
  };

  const requestContentImageUpload = (insertAfterIndex = null) => {
    if (disabled) return;
    insertAfterRef.current = Number.isInteger(insertAfterIndex) ? insertAfterIndex : null;
    contentImageRef.current?.click();
  };

  const handleContentImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const insertAfterIndex = insertAfterRef.current;
    insertAfterRef.current = null;

    if (!file || disabled) return;
    if (!file.type.startsWith('image/')) {
      alert(t('이미지 파일만 등록할 수 있어.'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('이미지는 5MB 이하만 업로드할 수 있어.'));
      return;
    }

    try {
      setUploadingImage(true);
      const uploaded = await uploadNoticeImage(file);
      const imageUrl = String(uploaded?.url || '').trim();
      if (!imageUrl) throw new Error(t('업로드 URL을 받지 못했어.'));

      setContentBlocks((prev) => {
        const current = normalizeLoungeEditorBlocks(prev);
        const imageBlock = {
          id: uid(),
          type: 'image',
          url: imageUrl,
          alt: '',
          display: { widthPercent: 100, heightPx: 280, objectFit: 'clip' },
        };
        const next = [...current];
        const targetIndex = Number.isInteger(insertAfterIndex)
          ? Math.max(0, Math.min(insertAfterIndex + 1, next.length))
          : next.length;

        next.splice(targetIndex, 0, imageBlock);
        if (!next[targetIndex + 1] || next[targetIndex + 1].type !== 'text') {
          next.splice(targetIndex + 1, 0, createLoungeTextBlock());
        }
        return next;
      });
    } catch (err) {
      alert(t('이미지 업로드 실패: {message}', { message: err?.message || 'unknown error' }));
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-5">
      <div className="fixed inset-0 bg-black/50" onClick={disabled ? undefined : onClose} />

      <div className="relative w-full sm:w-[1120px] max-w-[calc(100vw-24px)] sm:max-w-[96vw] max-h-[calc(100dvh-24px)] sm:max-h-[92vh] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            <div className="font-semibold">{title}</div>
            <div className="mt-1 text-xs text-gray-500">{t('라운지와 동일한 블로그형 에디터로 텍스트 스타일·링크·이미지를 편집할 수 있습니다.')}</div>
          </div>
          <button onClick={onClose} disabled={disabled} className="px-2 py-1 rounded border border-gray-200 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed">{t('닫기')}</button>
        </div>

        <div className="p-4 sm:p-5 overflow-y-auto max-h-[calc(100dvh-150px)] sm:max-h-[80vh]">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 mb-5">
            <div>
              <label className="text-xs text-gray-500">{t('제목')}</label>
              <input value={form.title} disabled={disabled} onChange={(e) => update({ title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500 disabled:bg-gray-100" placeholder={t('공지 제목')} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">{t('언어')}</label>
                <select value={form.language} disabled={disabled} onChange={(e) => update({ language: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-white disabled:bg-gray-100">
                  <option value="EN">EN</option>
                  <option value="KR">KR</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">{t('작성자')}</label>
                <input value={form.writer || ''} disabled={disabled} onChange={(e) => update({ writer: e.target.value })} className="mt-1 w-full px-3 py-2 rounded border border-gray-200 focus:outline-none focus:border-gray-500 disabled:bg-gray-100" placeholder="writer" />
              </div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-4 items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.isPinned} disabled={disabled} onChange={(e) => update({ isPinned: e.target.checked })} />
              {t('상단 고정(📌)')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.isPublished} disabled={disabled} onChange={(e) => update({ isPublished: e.target.checked })} />
              {t('게시 상태')}
            </label>
          </div>

          <div className="mb-2 flex items-end justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-gray-900">{t('본문 에디터')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('글자를 선택한 뒤 크기·굵기·색상·정렬·링크를 설정하고, 원하는 위치에 이미지를 넣을 수 있습니다.')}</div>
            </div>
            {uploadingImage ? <div className="text-xs font-semibold text-blue-600">{t('이미지 업로드중...')}</div> : null}
          </div>

          <LoungeRichEditor
            value={contentBlocks}
            onChange={setContentBlocks}
            disabled={disabled}
            uploading={uploadingImage}
            onRequestImageUpload={requestContentImageUpload}
          />
          <input ref={contentImageRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleContentImageChange} />

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button type="button" onClick={onClose} disabled={disabled} className="px-4 py-2 rounded border border-gray-200 hover:border-gray-400 disabled:opacity-50">{t('취소')}</button>
            <button type="button" onClick={submit} disabled={disabled} className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? t('저장중...') : uploadingImage ? t('업로드중...') : t('저장')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
